import { translate } from '../../Services/translation';
import React, { useEffect, useState } from 'react';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { flex1 } from '../../styling/utils/flex';
import { text, fontFamilyBold } from '../../styling/mixins/typography';
import { getColor } from '../../styling/mixins/colors';
import CheckoutSectionFooter from './utils/Checkout.SectionFooter';
import CheckoutSection from './utils/Checkout.Section';
import { updateOrderSummary } from '../../Actions/OrderSummary.action';
const StyledPriceRow = styled.div`
  display: flex;
  ${flex1}
  justify-content: space-between;
  ${text('large')}
  margin-bottom: 0.4rem;
  &:last-child {
    margin-bottom: unset;
  }
`;

const StyledDiscountRow = styled.div`
  display: flex;
  ${flex1}
  justify-content: space-between;
  ${text('large')}
  margin-bottom: 0.4rem;
  color: ${getColor('primary')};
  &:last-child {
    margin-bottom: unset;
  }
`;

const StyledGrandTotal = styled.div`
  ${text('large')}
  ${fontFamilyBold};
  display: flex;
  ${flex1}
  justify-content: space-between;
  margin-bottom: 0.4rem;
  &:last-child {
    margin-bottom: unset;
  }
`;

declare global {
  interface Window {
    _klarnaCheckout: (callback: (api: any) => void) => void;
    Vipps: any;
  }
}

const CheckoutOrderSummary = () => {
  const cart = useTypedSelector((state) => state.cart);
  const checkout = useTypedSelector((state) => state.checkout);
  const orderSummary = useTypedSelector((state) => state.orderSummary);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateOrderSummary(orderSummary.shippingMethodCost));
  }, [cart]);


  useEffect(() => {
    window.Vipps = window.Vipps || {};
    window.Vipps.shipping_option_selected = function (data) {
      dispatch(updateOrderSummary(data.price.fractionalDenomination / 100));
    }
  }, [dispatch, cart, orderSummary]);

  useEffect(() => {
    if (!checkout.payload.paymentWidget) return;

    const setupKlarnaListener = () => {
      if (window._klarnaCheckout) {
        window._klarnaCheckout(function (api) {
          api.on({
            'shipping_option_change': function (data) {
              dispatch(updateOrderSummary(data.price / 100));
            }
          });
        });
        return true;
      }
      return false;
    };

    if (!setupKlarnaListener()) {
      // If failed, set up observer to wait for Klarna
      const observer = new MutationObserver((mutations, obs) => {
        if (setupKlarnaListener()) {
          obs.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => observer.disconnect();
    }
  }, [checkout.payload.paymentWidget, dispatch, cart, orderSummary]);

  return (
    <CheckoutSection>
      <CheckoutSectionFooter>
        {checkout.payload.isBusinessCustomer &&
          <StyledPriceRow>
            <span>{translate('checkout.order.sum')}</span>
            <span>{cart.orderTotal}</span>
          </StyledPriceRow>
        }
        {cart.discount && (
          <StyledDiscountRow>
            <span>{translate('checkout.order.saved')}</span>
            <span>{cart.discount}</span>
          </StyledDiscountRow>
        )}
        {!checkout.payload.isBusinessCustomer && (
          <StyledPriceRow>
            <span>{translate('checkout.order.grandtotal.exl.delivery')}</span>
            <span>{cart.grandTotal}</span>
          </StyledPriceRow>
        )}
        <StyledPriceRow>
          <span>{translate('checkout.order.shipping')}</span>
          <span>{checkout.payload.isBusinessCustomer ?
            cart.deliveryCost :
            orderSummary.shippingMethodCost ?
              orderSummary.formattedShippingMethodCost :
              translate('checkout.b2c.delivery.dynamic').replace('{MinShippingCost}', orderSummary.formattedMinShippingCost)}
          </span>
        </StyledPriceRow>
        {cart.paymentCost && (
          <StyledPriceRow>
            <span>{translate('checkout.order.fee')}</span>
            <span>{cart.paymentCost}</span>
          </StyledPriceRow>
        )}
        {checkout.payload.isBusinessCustomer &&
          <StyledPriceRow>
            <span>{translate('checkout.order.vat')}</span>
            <span>{cart.vat}</span>
          </StyledPriceRow>
        }
        <StyledGrandTotal>
          <span>{checkout.payload.isBusinessCustomer ? translate('checkout.order.grandTotal') : translate('checkout.order.grandtotal.inl.delivery')}</span>
          <span>{checkout.payload.isBusinessCustomer ?
            cart.grandTotal :
            orderSummary.formattedGrandTotalWithShippingCost} </span>
        </StyledGrandTotal>
      </CheckoutSectionFooter>
    </CheckoutSection>
  );
};

export default CheckoutOrderSummary;
